/** What someone may do in the portal, weakest first. Names match the API's `PortalRole` exactly. */
export const PORTAL_ROLES = [
  { value: 'Member', label: 'Member' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Administrator', label: 'Administrator' },
] as const

export type PortalRole = (typeof PORTAL_ROLES)[number]['value']

export const roleLabel = (role: PortalRole) =>
  PORTAL_ROLES.find((item) => item.value === role)?.label ?? role

/** Where a portal user sits between being invited and being turned off. Already a plain word — no label map needed. */
export type UserStatus = 'Invited' | 'Active' | 'Disabled'

/**
 * One directory account matching `GET /api/users/directory-search`, for a form to pick from instead of
 * typing a name and email by hand.
 */
export type DirectorySearchResult = {
  objectId: string
  displayName: string
  email: string
  /** Already has a portal user record, active or not — informational only, picking them is still fine. */
  isInvited: boolean
}

/** `GET /api/users/me`, verbatim. Mapped to {@link User} before it reaches the UI. */
export type CurrentUserResponse = {
  id: string
  email: string
  displayName: string | null
  roles: PortalRole[]
  status: UserStatus
  personId: string | null
  personName: string | null
  isActive: boolean
  /** False while an invitation is outstanding: nobody has signed in against it yet. */
  isLinkedToDirectory: boolean
  lastSignedInAt: string | null
  createdAt: string
  updatedAt: string | null
  eTag: string | null
}

/**
 * The signed-in person as the app uses them. A local record on top of the Entra
 * identity: holding a company token is not the same as having portal access,
 * and the roles here are the portal's, not the directory's.
 */
export type User = {
  id: string
  /** Display name where there is one, falling back to the email so this is never blank. */
  name: string
  email: string
  roles: PortalRole[]
  status: UserStatus
  /** The roster person this login belongs to, when an administrator linked one. */
  personId: string | null
  personName: string | null
  lastSignedInAt: string | null
  avatarUrl?: string
}
